import { doc, setDoc, getDoc, collection, getDocs, DocumentData, QueryDocumentSnapshot, query, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, defaultDb, storage } from '../firebase/config';
import { UserData, UserPrivateData, UserPublicData, FCMTokenData } from '../types/user';
import { ActiveSubscription } from '../types/subscription';

export interface UserSubscription {
  userId: string;
  subscriptionType: string;
  created: any;
  lastUpdated: any;
  status: string;
  issuedBy: string;
  stripeSubscriptionId?: string;
}

export interface RegisterUserData extends UserData {
  email: string;
}

export type CampaignSignupRow = {
  userId: string;
  username: string;
  displayName: string;
  email?: string;
  signupAt: Date | null;
};

function parseCampaignSignUpsListEntries(
  raw: unknown,
): Array<{ userId: string; at: unknown }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const userId = o.userId;
      if (typeof userId !== "string" || !userId.trim()) return null;
      return { userId: userId.trim(), at: o.at };
    })
    .filter((x): x is { userId: string; at: unknown } => x !== null);
}

export async function getUserPublicData(userId: string): Promise<UserPublicData | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }
    return userDoc.data() as UserPublicData;
  } catch (error) {
    console.error('Error fetching user public data:', error);
    return null;
  }
}

export class UserService {
  private splitUserData(userData: UserData): { publicData: UserPublicData; privateData: UserPrivateData } {
    const { email, dob, name, surname, gender, country, deviceIDs, fcmTokens, ...publicData } = userData;
    
    const privateData: UserPrivateData = {
      email,
      dob,
      name,
      surname,
      gender,
      country,
      deviceIDs: deviceIDs || [],
      fcmTokens: fcmTokens || []
    };

    return { publicData, privateData };
  }

  // Helper function to convert Firestore Timestamps to Date objects
  private convertTimestampToDate(timestamp: any): Date | undefined {
    if (!timestamp) return undefined;
    
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    
    try {
      return new Date(timestamp);
    } catch {
      return undefined;
    }
  }

  private readonly publicFields = ['username', 'reason', 'role', 'sport', 'trainingSport', 'cycleDays', 'blockDays', 'sleepGoal', 'loadGoal', 'weeklyLoadGoal', 'performanceGoals', 'healthGoals', 'performanceGoals', 'healthGoals', 'campaignCode', 'imageUrl', 'onboardingComplete', 'customTrainingTags', 'customSessionTypes', 'orgIds', 'organisationId'] as const;
  private readonly privateFields = ['email', 'dob', 'name', 'surname', 'gender', 'country', 'deviceIDs', 'fcmTokens'] as const;

  private pickFields<T extends Record<string, any>, K extends keyof T>(
    obj: T, 
    fields: readonly K[]
  ): Pick<T, K> {
    const result = {} as Pick<T, K>;
    for (const field of fields) {
      if (obj[field] !== undefined) {
        result[field] = obj[field];
      }
    }
    return result;
  }

  /**
   * Upload profile image to Firebase Storage
   */
  async uploadProfileImage(userId: string, imageFile: File): Promise<string> {
    try {
      const imageRef = ref(storage, `profile-images/${userId}`);
      await uploadBytes(imageRef, imageFile);
      const imageUrl = await getDownloadURL(imageRef);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw new Error('Failed to upload profile image');
    }
  }

  /**
   * Create user document with separated public/private data
   */
  async createUserDocument(userId: string, userData: UserData): Promise<void> {
    try {
      const { publicData, privateData } = this.splitUserData(userData);
      const userRef = doc(db, 'users', userId);
      const existing = await getDoc(userRef);
      const publicPayload: DocumentData = { ...publicData };
      if (!existing.exists()) {
        publicPayload.accountCreatedAt = serverTimestamp();
      }

      // Save public data to main document
      await setDoc(userRef, publicPayload);

      // Save private data to subcollection
      await setDoc(doc(db, 'users', userId, 'private', 'data'), privateData);
    } catch (error) {
      console.error('Error creating user document:', error);
      throw new Error('Failed to create user profile');
    }
  }

  /**
   * Get complete user data by merging public and private data
   */
  async getUserData(userId: string): Promise<UserData | null> {
    try {
      // Get public data from main document
      const publicDoc = await getDoc(doc(db, 'employees', userId));
      if (!publicDoc.exists()) {
        return null;
      }
      const publicData = publicDoc.data() as UserPublicData;

      // Get private data from subcollection
      const privateDoc = await getDoc(doc(db, 'employees', userId));
      if (!privateDoc.exists()) {
        return null;
      }
      const privateData = privateDoc.data() as UserPrivateData;

      // Convert Firestore Timestamps to Date objects for FCM tokens
      if (privateData.fcmTokens) {
        privateData.fcmTokens = privateData.fcmTokens.map(token => ({
          ...token,
          createdAt: this.convertTimestampToDate(token.createdAt) || new Date(),
          lastUsed: this.convertTimestampToDate(token.lastUsed)
        }));
      }

      // Merge and return complete user data
      return { ...publicData, ...privateData } as UserData;
    } catch (error) {
      console.error('Error getting user data:', error);
      throw new Error('Failed to retrieve user data');
    }
  }

    /**
     * Get user subscription data
     */
    async getUserSubscription(userId: string): Promise<ActiveSubscription | null> {
        try {
        const subscriptionDoc = await getDoc(doc(db, "activeSubscriptions", userId));
        
        if (!subscriptionDoc.exists()) {
            return null;
        }

        // console.log(`[UserService] Fetched subscription for user ${userId}:`, subscriptionDoc.data());
        
        return subscriptionDoc.data() as ActiveSubscription;
        } catch (error) {
        console.error(`[UserService] Error fetching subscription for user ${userId}:`, error);
        return null;
        }
    }

    /**
   * Get user subscription data
   */
  async getStripeSubscription(userId: string, subscriptionId: string): Promise<UserSubscription | null> {
    try {
      const subscriptionDoc = await getDoc(doc(defaultDb, `customers/${userId}/subscriptions/${subscriptionId}`));
      if (!subscriptionDoc.exists()) {
        return null;
      }
      return subscriptionDoc.data() as any;
    } catch (error) {
      console.error('Error getting Stripe subscription:', error);
      throw new Error('Failed to retrieve Stripe subscription');
    }
  }

  // get the users stripe customer id from firestore
  async getStripeCustomerId(userId: string): Promise<string | null> {
    try {
      const customerDoc = await getDoc(doc(defaultDb, `customers/${userId}`));
      if (!customerDoc.exists()) {
        return null;
      } 
        const customerData = customerDoc.data();    
        return customerData.stripeId || null;
    } catch (error) {
      console.error('Error getting Stripe customer ID:', error);
      throw new Error('Failed to retrieve Stripe customer ID');
    }
  }

  /**
   * Create user subscription record
   */
  async createUserSubscription(userId: string, subscriptionData: UserSubscription): Promise<void> {
    try {
      await setDoc(doc(db, 'userSubscriptions', userId), subscriptionData);
    } catch (error) {
      console.error('Error creating user subscription:', error);
      throw new Error('Failed to create user subscription');
    }
  }

  /**
   * Get both user data and subscription data in a single call
   */
  async getUserProfile(userId: string): Promise<{ userData: UserData | null; subscription: ActiveSubscription | null }> {
    try {
      const [userData, subscription] = await Promise.all([
        this.getUserData(userId),
        this.getUserSubscription(userId)
      ]);
      
      return { userData, subscription };
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw new Error('Failed to retrieve user profile');
    }
  }

  /**
   * Complete user registration with profile data and optional image
   */
    async completeUserRegistration(
        userId: string, 
        userData: UserData, 
        profileImage?: File
    ): Promise<void> {
        try {
        let finalUserData = { ...userData };

        // Upload profile image if provided
        if (profileImage) {
            const imageUrl = await this.uploadProfileImage(userId, profileImage);
            finalUserData = { ...finalUserData, imageUrl };
        }

        // Create user document in Firestore with separated data
        await this.createUserDocument(userId, finalUserData);
        } catch (error) {
        console.error('Error completing user registration:', error);
        throw error;
        }
    }

  /**
   * Update user profile data (handles both public and private updates)
   */
  async updateUserProfile(userId: string, updates: Partial<UserData>): Promise<void> {
    try {
      // Extract public and private updates with proper typing
      const publicUpdates = this.pickFields(updates, this.publicFields);
      const privateUpdates = this.pickFields(updates, this.privateFields);

      console.log('UserService.updateUserProfile - Public updates:', publicUpdates);
      console.log('UserService.updateUserProfile - Private updates:', privateUpdates);

      // Update public data if any public fields are included
      if (Object.keys(publicUpdates).length > 0) {
        console.log('Updating public data in Firestore:', publicUpdates);
        await setDoc(doc(db, 'users', userId), publicUpdates, { merge: true });
      }

      // Update private data if any private fields are included
      if (Object.keys(privateUpdates).length > 0) {
        console.log('Updating private data in Firestore:', privateUpdates);
        await setDoc(doc(db, 'users', userId, 'private', 'data'), privateUpdates, { merge: true });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }



  /**
   * Get all users (for admin purposes - use with caution)
   */
  async getAllUsers(): Promise<Array<{ id: string; userData: UserData }>> {
    try {
      // Get all public documents first
      const publicDocsSnapshot = await getDocs(collection(db, 'users'));
      
      // Then get the private data for each user
      const usersWithData = await Promise.all(
        publicDocsSnapshot.docs.map(async (publicDoc: QueryDocumentSnapshot<DocumentData>) => {
          const userId = publicDoc.id;
          const userData = await this.getUserData(userId);
          return userData ? { id: userId, userData } : null;
        })
      );
      
      // Filter out null values
      return usersWithData.filter((user): user is { id: string; userData: UserData } => user !== null);
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to retrieve all users');
    }
  }

  /**
   * Build one admin signup table row. Pass signupAtOverride undefined to use
   * profile accountCreatedAt; pass Date or null to lock the sign-up column (tracked list).
   */
  private async buildCampaignSignupRow(
    userId: string,
    signupAtOverride: Date | null | undefined,
  ): Promise<CampaignSignupRow> {
    let username = userId;
    let displayName = userId;
    let email: string | undefined;
    let signupAt: Date | null = null;

    try {
      const pubSnap = await getDoc(doc(db, 'users', userId));
      if (pubSnap.exists()) {
        const data = pubSnap.data() as UserPublicData & {
          accountCreatedAt?: unknown;
        };
        username = data.username?.trim() || userId;
        displayName = username;
        if (signupAtOverride === undefined) {
          signupAt =
            this.convertTimestampToDate(data.accountCreatedAt) ?? null;
        } else {
          signupAt = signupAtOverride;
        }
      } else if (signupAtOverride !== undefined) {
        signupAt = signupAtOverride;
      }
    } catch (e) {
      console.error(`[UserService] Public read for signup row ${userId}:`, e);
      if (signupAtOverride !== undefined) {
        signupAt = signupAtOverride;
      }
    }

    try {
      const privateDoc = await getDoc(
        doc(db, 'users', userId, 'private', 'data'),
      );
      if (privateDoc.exists()) {
        const priv = privateDoc.data() as UserPrivateData;
        const name = `${priv.name || ''} ${priv.surname || ''}`.trim();
        if (name) displayName = name;
        email = priv.email;
      }
    } catch (privErr) {
      console.error(
        `[UserService] Private read for signup row ${userId}:`,
        privErr,
      );
    }

    return { userId, username, displayName, email, signupAt };
  }

  /**
   * Get user count for a specific campaign code
   */
  async getUserCountForCampaign(campaignCode: string): Promise<number> {
    try {
      // Query public documents that have the campaign code
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('campaignCode', '==', campaignCode));
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error(`Error counting users for campaign ${campaignCode}:`, error);
      return 0;
    }
  }

  /**
   * Users who registered with this campaign code (public + private read for admin UI).
   */
  async getUsersForCampaign(campaignCode: string): Promise<CampaignSignupRow[]> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('campaignCode', '==', campaignCode));
      const querySnapshot = await getDocs(q);

      const rows = await Promise.all(
        querySnapshot.docs.map((d) =>
          this.buildCampaignSignupRow(d.id, undefined),
        ),
      );

      rows.sort((a, b) => {
        const ta = a.signupAt?.getTime() ?? 0;
        const tb = b.signupAt?.getTime() ?? 0;
        return tb - ta;
      });

      return rows;
    } catch (error) {
      console.error(
        `Error listing users for campaign ${campaignCode}:`,
        error,
      );
      throw new Error('Failed to load campaign signups');
    }
  }

  /**
   * Resolve signUpsList from a tracked campaign document into table rows.
   */
  async hydrateCampaignSignUpsList(rawList: unknown): Promise<CampaignSignupRow[]> {
    const entries = parseCampaignSignUpsListEntries(rawList);
    const sorted = [...entries].sort((a, b) => {
      const ta = this.convertTimestampToDate(a.at)?.getTime() ?? 0;
      const tb = this.convertTimestampToDate(b.at)?.getTime() ?? 0;
      return tb - ta;
    });

    return Promise.all(
      sorted.map((e) =>
        this.buildCampaignSignupRow(
          e.userId,
          this.convertTimestampToDate(e.at) ?? null,
        ),
      ),
    );
  }

  /**
   * Handle campaign code cleanup
   */
  cleanupCampaignCode(campaignCode?: string): void {
    if (campaignCode) {
      localStorage.removeItem('campaignCode');
    }
  }

  /**
   * Get user's FCM tokens
   */
  async getUserFCMTokens(userId: string): Promise<FCMTokenData[]> {
    try {
      const userData = await this.getUserData(userId);
      const tokens = userData?.fcmTokens || [];
      console.log(`Retrieved ${tokens.length} FCM tokens for user ${userId}`);
      console.log('Retrieved tokens:', tokens.map(t => ({ 
        deviceId: t.deviceId, 
        deviceName: t.deviceName, 
        enabled: t.enabled,
        token: t.token.substring(0, 10) + '...' 
      })));
      return tokens;
    } catch (error) {
      console.error('Error getting user FCM tokens:', error);
      throw new Error('Failed to retrieve FCM tokens');
    }
  }

  /**
   * Get all enabled FCM tokens for a user
   */
  async getEnabledFCMTokens(userId: string): Promise<string[]> {
    try {
      const tokens = await this.getUserFCMTokens(userId);
      return tokens
        .filter(tokenData => tokenData.enabled)
        .map(tokenData => tokenData.token);
    } catch (error) {
      console.error('Error getting enabled FCM tokens:', error);
      throw new Error('Failed to retrieve enabled FCM tokens');
    }
  }

  /**
   * Add or update FCM token for a user
   */
  async addFCMTokenToUser(userId: string, tokenData: FCMTokenData): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error('User data not found');
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];
      
      // Check if token already exists (same token or same device)
      const existingTokenIndex = existingTokens.findIndex(
        t => t.token === tokenData.token || t.deviceId === tokenData.deviceId
      );
      
      console.log(`Checking if token exists for device ${tokenData.deviceId} (${tokenData.deviceName})`);
      console.log(`Existing tokens count: ${existingTokens.length}`);
      console.log(`Found existing token at index: ${existingTokenIndex}`);
      console.log(`New token starts with: ${tokenData.token.substring(0, 10)}...`);
      if (existingTokenIndex >= 0) {
        console.log(`Existing token starts with: ${existingTokens[existingTokenIndex].token.substring(0, 10)}...`);
        console.log(`Tokens match: ${existingTokens[existingTokenIndex].token === tokenData.token}`);
        console.log(`Device IDs match: ${existingTokens[existingTokenIndex].deviceId === tokenData.deviceId}`);
      }
      
      if (existingTokenIndex >= 0) {
        // Update existing token
        existingTokens[existingTokenIndex] = {
          ...existingTokens[existingTokenIndex],
          ...tokenData,
          lastUsed: new Date()
        };
      } else {
        // Add new token
        existingTokens.push(tokenData);
      }
      
      // Update user's FCM tokens
      await this.updateUserProfile(userId, { fcmTokens: existingTokens });
      
      console.log(`FCM token added/updated for user ${userId} on device ${tokenData.deviceName}`);
      console.log(`Total FCM tokens for user ${userId}: ${existingTokens.length}`);
      console.log('All FCM tokens:', existingTokens.map(t => ({ 
        deviceId: t.deviceId, 
        deviceName: t.deviceName, 
        enabled: t.enabled,
        token: t.token.substring(0, 10) + '...' 
      })));
    } catch (error) {
      console.error('Error adding FCM token to user:', error);
      throw new Error('Failed to add FCM token');
    }
  }

  /**
   * Remove invalid FCM token from user's list
   */
  async removeInvalidFCMToken(userId: string, invalidToken: string): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error('User data not found');
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];
      
      // Filter out the invalid token
      const updatedTokens = existingTokens.filter(tokenData => tokenData.token !== invalidToken);
      
      // Update user's FCM tokens
      await this.updateUserProfile(userId, { fcmTokens: updatedTokens });
      
      console.log(`Removed invalid FCM token for user ${userId}`);
    } catch (error) {
      console.error('Error removing invalid FCM token:', error);
      throw new Error('Failed to remove invalid FCM token');
    }
  }

  /**
   * Toggle notification enable/disable for a specific device
   */
  async toggleDeviceNotifications(userId: string, deviceId: string, enabled: boolean): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error('User data not found');
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];
      
      // Find and update the specific device
      const updatedTokens = existingTokens.map(tokenData => 
        tokenData.deviceId === deviceId 
          ? { ...tokenData, enabled, lastUsed: new Date() }
          : tokenData
      );
      
      // Update user's FCM tokens
      await this.updateUserProfile(userId, { fcmTokens: updatedTokens });
      
      console.log(`${enabled ? 'Enabled' : 'Disabled'} notifications for device ${deviceId}`);
    } catch (error) {
      console.error('Error toggling device notifications:', error);
      throw new Error('Failed to toggle device notifications');
    }
  }

  /**
   * Update last used timestamp for an FCM token
   */
  async updateFCMTokenLastUsed(userId: string, token: string): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        return; // Silently fail if user not found
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];
      
      // Update the last used timestamp for the specific token
      const updatedTokens = existingTokens.map(tokenData => 
        tokenData.token === token 
          ? { ...tokenData, lastUsed: new Date() }
          : tokenData
      );
      
      // Only update if we found the token
      if (updatedTokens.some(t => t.lastUsed && t.token === token)) {
        await this.updateUserProfile(userId, { fcmTokens: updatedTokens });
      }
    } catch (error) {
      console.error('Error updating FCM token last used:', error);
      // Don't throw here as this is a non-critical operation
    }
  }
}

export async function getUserDisplayName(
  userId: string
): Promise<string> {
  try {
    const privateDoc = await getDoc(
      doc(db, "users", userId, "private", "data")
    );

    if (!privateDoc.exists()) {
      return "Unknown User";
    }

    const privateData = privateDoc.data() as UserPrivateData;
    const name = privateData?.name || "";
    const surname = privateData?.surname || "";

    // Join name and surname, trim any extra spaces
    const displayName = `${name} ${surname}`.trim();

    // Return "Unknown User" if both name and surname are empty
    return displayName || "Unknown User";
  } catch (error) {
    console.error(`Error fetching user display name for user: ${userId}`, error);
    return "Unknown User";
  }
}

// Export singleton instance
export const userService = new UserService(); 